import { useState } from "react";
import { useNavigate } from "react-router";

import { queryKeys } from "@/api/hooks";
import { isTerminalQueryError } from "@/api/queryClient";
import {
  useSummaryTargetCatalogQuery,
  type SummaryPromptAsset,
  type SummaryTargetCatalog,
} from "@/api/summaryTargets";
import { useRetryPausedQuery } from "@/api/useRetryPausedQuery";
import { useCapability } from "@/auth/SessionContext";
import { lifecycleRefusal } from "@/lib/archiveRefusal";
import { draftExampleProblems } from "@/lib/summaryExamples";
import { evaluationsOfGroup, summaryTargetName } from "@/lib/summaryTargets";
import { findNamedRow, namedRows, updateNamedDocument } from "@/lib/namedParts";
import { PageHeader } from "@/shell/PageHeader";
import { Button } from "@/ui/Button";
import { Notice } from "@/ui/Notice";
import { Tooltip } from "@/ui/Tooltip";
import { SaveStatusLine } from "@/ui/library/AssetEditor";
import {
  LifecycleDialog,
  type LifecycleMode,
} from "@/ui/library/LifecycleDialog";
import { LoadingState, PartialDataNotice, UnavailableState } from "@/ui/states";

import { NamedAssetMeta } from "./NamedAssetChrome";
import {
  NamedDocumentActions,
  NamedDocumentEditor,
  isAuthorableDocumentSet,
} from "./NamedDocumentEditor";
import {
  NamedConflictRecovery,
  NamedProblems,
  NamedRefusal,
} from "./NamedDraftStates";
import { NamedReadFailureState } from "./NamedReadFailure";
import { NewSummaryPromptDialog } from "./NewSummaryPromptDialog";
import { SummaryExamplesPanel } from "./SummaryExamples";
import { useNamedDraft } from "./useNamedDraft";

/**
 * One Summary prompt, open for authoring (#863).
 *
 * ## WHAT IT REPLACES, AND WHY
 *
 * `AssetEditor` + `PartsEditor`, which is what a Summary prompt reached before.
 * That pair asks for a "Part key" in a free-text input and renders structured
 * documents into a writable raw-JSON textarea. For this family the reference is
 * explicit — one named prompt field, Back, Details, Save — and the raw-key
 * escape hatch is the same one the 2026-08-25 canonical amendment already
 * removed for main concern and pathways. This is that surface for Summary, built
 * on the same `useNamedDraft` so the concurrency rules are #33's, unchanged: the
 * pin is taken at load, a background refetch never re-seeds a dirty draft, a
 * clean draft re-seeds forward only, and a stale save recovers through the
 * shared `ConflictRecovery`.
 *
 * ## THE HEADING IS THE NAME; THE IDENTITY IS THE TARGET
 *
 * The reference has one string for both, because its prompts have no name apart
 * from their section. Here they do: a clinician names the asset and the SERVER
 * owns which prompt it is. Showing only the name would hide the fact that
 * decides what this text does at runtime, and showing only the target would
 * rename the clinician's asset. So the name is the `h1` and the target — the
 * pack's own slug, in monospace, beside the shape it is — sits directly under
 * it, with the exact file it writes in Details.
 *
 * ## EVERY OTHER PART SURVIVES A SAVE, AND THAT IS STRUCTURAL
 *
 * `useNamedDraft` publishes the WHOLE parts array; this surface edits the
 * `prompt` row's documents and passes the rest through untouched. So a Summary
 * prompt's example set — the ordered pairs #915 versions and #916 authors — is
 * preserved by the shape of the editor rather than by a rule somebody has to
 * remember: `SummaryExamplesPanel` writes the `<language>/examples` part of this
 * same draft, and the ONE Save in the header publishes both as one immutable
 * version. That is also why an unsaved pair survives a prompt edit, a refused
 * Save and a stale-save recovery — all three keep the draft, and the pairs are
 * in it.
 */
export function SummaryPromptPage({ asset }: { asset: SummaryPromptAsset }) {
  const navigate = useNavigate();
  const state = useNamedDraft(asset);
  const targets = useSummaryTargetCatalogQuery();
  const retryTargets = useRetryPausedQuery(queryKeys.summaryTargetCatalog);
  const write = useCapability("library.write");
  const restore = useCapability("library.restore");
  const [lifecycle, setLifecycle] = useState<LifecycleMode | null>(null);
  const [copying, setCopying] = useState(false);
  // Why the last Save press published nothing. Held rather than derived so it
  // appears on the PRESS and clears on the next edit, and never as a permanent
  // sentence about a draft the clinician has not tried to save yet.
  const [blockedSave, setBlockedSave] = useState<string | null>(null);

  const target = asset.summaryTarget ?? null;
  const breadcrumbs = [
    { label: "Clinical library", to: "/library" },
    { label: "Summary", to: "/library/summary" },
  ];

  if (state.status === "loading") {
    return (
      <>
        <PageHeader breadcrumbs={breadcrumbs} title={asset.name} />
        <LoadingState label="Loading prompt content" rows={4} />
      </>
    );
  }

  if (state.status === "error") {
    return (
      <>
        <PageHeader breadcrumbs={breadcrumbs} title={asset.name} />
        <NamedReadFailureState
          failure={state.failure}
          isPaused={state.isPaused}
          retry={state.retry}
          subject="Prompt content"
          title="This prompt's content could not be loaded"
          description={`The Editor Service did not return the content at ${asset.latestVersion}, so there is nothing to edit yet.`}
          deniedDescription="You are not authorized to read this prompt's content."
          offlineDescription={`The content at ${asset.latestVersion} could not be reached, so there is nothing to edit yet. It loads on its own when the connection returns.`}
        />
      </>
    );
  }

  if (state.status === "unsupported") {
    return (
      <>
        <PageHeader breadcrumbs={breadcrumbs} title={asset.name} />
        <UnavailableState
          title="This asset's content is not a prompt"
          description="A Summary prompt is authored as a named prompt document, and this asset's stored content is a different shape."
        />
      </>
    );
  }

  const { editor } = state;
  const rows = namedRows(editor.parts, "summary_prompt");
  const prompt = findNamedRow(rows, "prompt");
  const authorable = prompt !== null && isAuthorableDocumentSet(prompt.documents);
  // Read off the DRAFT alone (`lib/summaryExamples`): an empty example input is
  // refused by `ExampleInput`'s own `min_length=1`, and knowing that needs no
  // service answer. The floor an authored set may not go below does, and that
  // one stays inside the panel with the read that supplies it.
  const exampleProblems = draftExampleProblems(editor.parts);

  const archiveMode: LifecycleMode = asset.capabilities.archived
    ? "unarchive"
    : "archive";
  // Direction-specific caller gate (#647): archiving is `library.write`,
  // restoring is the admin/operations `library.restore` the ratified contract
  // (§2.1) reserves — the same split the service enforces on the two routes.
  const archiveRefusal = lifecycleRefusal(
    asset,
    archiveMode === "unarchive" ? restore.reason : write.reason,
  );
  const archiveLabel = archiveMode === "archive" ? "Archive" : "Unarchive";

  const targetsUnread = targets.isPaused || targets.isError;
  const targetsFailure = targets.isPaused
    ? targets.failureReason
    : targets.isError
      ? targets.error
      : null;
  const directory = target ? directoryFor(targets.data, target) : null;

  return (
    <>
      <PageHeader
        breadcrumbs={[
          ...breadcrumbs,
          // The crumb is the TARGET, which is what the reference's third crumb
          // is and what a clinician navigated here looking for.
          { label: target ? summaryTargetName(target) : asset.name },
        ]}
        title={asset.name}
        description={<TargetLine target={target} />}
        meta={
          <NamedAssetMeta
            asset={asset}
            version={editor.pinned}
            identity={`summary/${asset.id}`}
            details={
              <>
                <div className="break-all">
                  target: {target ? summaryTargetName(target) : "not readable"}
                </div>
                <div className="break-all">
                  writes: {directory ?? "not reported"}
                </div>
              </>
            }
          />
        }
        actions={
          <>
            {asset.capabilities.seeded ? (
              <Tooltip content="Part of the base clinical library. The original stays as it is — make an editable copy to create your own version.">
                <Button variant="outline" onClick={() => setCopying(true)}>
                  Create editable copy
                </Button>
              </Tooltip>
            ) : null}
            <Tooltip content={archiveRefusal ?? `${archiveLabel} this prompt`}>
              <Button
                variant="outline"
                disabled={archiveRefusal !== null}
                onClick={() => setLifecycle(archiveMode)}
              >
                {archiveLabel}
              </Button>
            </Tooltip>
            <NamedDocumentActions
              readOnly={editor.readOnly || !authorable}
              dirty={editor.dirty}
              saving={editor.saving}
              onBack={() => void navigate("/library/summary")}
              onSave={() => {
                if (exampleProblems.length > 0) {
                  // NEVER A SILENT RETURN: the clinician pressed Save and is
                  // owed the reason. The per-row sentences are already on
                  // screen; this says why the press did nothing.
                  setBlockedSave(
                    `This version has ${exampleProblems.length === 1 ? "an example pair" : `${exampleProblems.length} example pairs`} with no input, which the pinned summary agent refuses. Nothing was published, and nothing on this page changed.`,
                  );
                  return;
                }
                setBlockedSave(null);
                editor.save();
              }}
            />
          </>
        }
      />

      <div className="space-y-4">
        <PartialDataNotice
          visible={targetsUnread}
          description="The Editor Service did not report which Summary targets this runtime reads, so the exact pack file this prompt writes is not shown. Which target this prompt claims, and everything else on this page, is unaffected."
          {...(targetsUnread && !isTerminalQueryError(targetsFailure)
            ? {
                action: (
                  <Button variant="outline" onClick={retryTargets}>
                    Try again
                  </Button>
                ),
              }
            : {})}
        />

        <NamedConflictRecovery editor={editor} />
        <NamedRefusal editor={editor} />
        <NamedProblems editor={editor} />

        {prompt === null ? (
          // A Summary prompt the service required a `prompt` document of, that
          // does not carry one. Stated rather than rendered as an empty editor:
          // an empty box would invite a clinician to write into a document this
          // version does not have, and Save would refuse.
          <UnavailableState
            title="This version carries no prompt document"
            description="A Summary prompt is authored as one prompt document per language, and this stored version has none. Its other content is unchanged and nothing on this screen alters it."
          />
        ) : (
          <NamedDocumentEditor
            documents={prompt.documents}
            readOnly={editor.readOnly}
            readOnlyReason={editor.readOnlyReason}
            problems={editor.problems}
            onChange={(documentId, text) =>
              editor.change(updateNamedDocument(editor.parts, documentId, text))
            }
          />
        )}

        {/* The ordered example pairs, BELOW the prompt and inside the same
            draft (#916). The reference has no such editor, and this is the one
            addition the S7 screen contract names for this screen. */}
        <SummaryExamplesPanel
          assetId={asset.id}
          version={editor.pinned}
          parts={editor.parts}
          readOnly={editor.readOnly}
          onChange={(next) => {
            setBlockedSave(null);
            editor.change(next);
          }}
        />

        {exampleProblems.length > 0 ? (
          // ALWAYS VISIBLE, not only after a refused Save: an empty input is
          // visible on the row that has it, and the reason the next Save will
          // not land belongs beside it rather than behind a press.
          <ul className="space-y-1">
            {exampleProblems.map((problem) => (
              <li
                key={`${problem.language}:${problem.index}`}
                className="text-sm text-destructive"
              >
                {problem.message}
              </li>
            ))}
          </ul>
        ) : null}

        {blockedSave === null ? null : (
          <Notice tone="destructive" role="alert">
            {blockedSave}
          </Notice>
        )}

        {editor.readOnly ? null : (
          <div className="flex flex-wrap items-center justify-end gap-3">
            <SaveStatusLine
              status={
                editor.saving
                  ? "saving"
                  : editor.failure !== null
                    ? "error"
                    : editor.dirty
                      ? "dirty"
                      : editor.lastSavedAt
                        ? "saved"
                        : "clean"
              }
              lastSavedAt={editor.lastSavedAt}
            />
            {/* CANCELLING AN UNSAVED EDIT, on this screen rather than only as a
                navigation side effect: Back leaves the page and a clinician who
                wants the stored text back needs a control that says so. It sits
                beside the save status because that line is what says there is
                something to cancel.

                SAVE IS NOT REPEATED HERE. It is in the page header, where the
                reference puts it and where `NamedDocumentActions` renders it for
                every other named editor — one screen with two Save buttons is
                two answers to "did I save?", and the header's is the one a
                clinician reaches from anywhere on a long prompt. */}
            <Button
              variant="outline"
              disabled={!editor.dirty || editor.saving}
              onClick={() => {
                setBlockedSave(null);
                editor.discard();
              }}
            >
              Discard
            </Button>
          </div>
        )}
      </div>

      {lifecycle ? (
        <LifecycleDialog
          asset={asset}
          mode={lifecycle}
          onClose={() => setLifecycle(null)}
        />
      ) : null}
      {copying ? (
        <NewSummaryPromptDialog
          copyFrom={asset}
          onClose={() => setCopying(false)}
        />
      ) : null}
    </>
  );
}

/**
 * WHICH prompt this is, under the name the clinician gave it.
 *
 * The slug is rendered verbatim and in monospace: it is the directory the pinned
 * engine opens and the word the pack, the runbook and the reference all use, so
 * a prettified label would be a second name for one clinical thing.
 */
function TargetLine({
  target,
}: {
  target: SummaryPromptAsset["summaryTarget"] | null;
}) {
  if (!target) {
    return (
      <span className="text-muted-foreground">
        This prompt&apos;s stored placement is not a Summary target this editor
        can read, so which prompt it is cannot be stated here.
      </span>
    );
  }
  return (
    <span className="flex flex-wrap items-center gap-2">
      <span>
        {target.kind === "section" ? "Report section" : "Diagnostic finding"}
      </span>
      <code
        dir="ltr"
        className="rounded bg-muted px-1 py-0.5 font-mono text-xs text-foreground"
      >
        {summaryTargetName(target)}
      </code>
    </span>
  );
}

/** The exact pack directory this target writes, as the SERVER expands it. */
function directoryFor(
  targets: SummaryTargetCatalog | undefined,
  target: NonNullable<SummaryPromptAsset["summaryTarget"]>,
): string | null {
  if (target.kind === "section") {
    return (
      targets?.sections.find((option) => option.section === target.section)
        ?.directory ?? null
    );
  }
  return (
    evaluationsOfGroup(targets, target.group).find(
      (option) => option.evaluation === target.evaluation,
    )?.directory ?? null
  );
}
